import axios from "axios";
import { clearAuth, getAuthItem } from "../../utils/authStorage";

const TIMEOUT = 30000;

// Attach token to all requests when available
axios.interceptors.request.use((config) => {
  const token = getAuthItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const handleApiError = (error) => {
  if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
    return { success: false, message: "Request timeout. Please try again." };
  }
  if (!error.response) {
    return { success: false, message: "Network error. Please check your connection." };
  }
  // Token expiry - clear session and redirect to login
  if (error?.response?.data?.message === "Token is expired") {
    clearAuth();
    window.location.href = "/";
    return;
  }
  return error?.response?.data ?? { success: false, message: "An error occurred." };
};

export const apiPost = async (url, params, headers = {}) => {
  try {
    const response = await axios.post(url, params, {
      headers: { "Content-Type": "application/json", ...headers },
      timeout: TIMEOUT,
    });
    return response?.data;
  } catch (error) {
    return handleApiError(error);
  }
};

/** POST that also returns HTTP status so callers can tell 200 business results from 400/404/500. */
export const apiPostWithMeta = async (url, params, headers = {}) => {
  try {
    const response = await axios.post(url, params, {
      headers: { "Content-Type": "application/json", ...headers },
      timeout: TIMEOUT,
    });
    const data = response?.data && typeof response.data === "object" ? response.data : { data: response?.data };
    return { httpStatus: response.status, ...data };
  } catch (error) {
    const handled = handleApiError(error);
    if (handled == null) return { httpStatus: error?.response?.status, success: false };
    return {
      httpStatus: error?.response?.status,
      isHttpError: true,
      ...handled,
    };
  }
};

export const apiGet = async (url, headers = {}, params = {}) => {
  try {
    const response = await axios.get(url, {
      headers: { "Content-Type": "application/json", ...headers },
      params: Object.keys(params).length ? params : undefined,
      timeout: TIMEOUT,
    });
    return response?.data;
  } catch (error) {
    return handleApiError(error);
  }
};

/** GET that also returns HTTP status so callers can tell 200 from 400/404/401/500. */
export const apiGetWithMeta = async (url, headers = {}, params = {}, timeout = TIMEOUT) => {
  try {
    const response = await axios.get(url, {
      headers: { "Content-Type": "application/json", ...headers },
      params: Object.keys(params).length ? params : undefined,
      timeout,
    });
    const data =
      response?.data && typeof response.data === "object"
        ? response.data
        : { data: response?.data };
    return { httpStatus: response.status, ...data };
  } catch (error) {
    const handled = handleApiError(error);
    if (handled == null) return { httpStatus: error?.response?.status, success: false };
    return {
      httpStatus: error?.response?.status,
      isHttpError: true,
      ...handled,
    };
  }
};

export const apiPut = async (url, params, headers = {}) => {
  try {
    const response = await axios.put(url, params, {
      headers: { "Content-Type": "application/json", ...headers },
      timeout: TIMEOUT,
    });
    return response?.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const apiPatch = async (url, params, headers = {}) => {
  try {
    const response = await axios.patch(url, params, {
      headers: { "Content-Type": "application/json", ...headers },
      timeout: TIMEOUT,
    });
    return response?.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const apiDelete = async (url, headers = {}) => {
  try {
    const response = await axios.delete(url, {
      headers: { "Content-Type": "application/json", ...headers },
      timeout: TIMEOUT,
    });
    return response?.data;
  } catch (error) {
    return handleApiError(error);
  }
};

/** DELETE with a JSON body. Auth header is added by the axios interceptor. */
export const apiDeleteWithBody = async (url, data, headers = {}) => {
  try {
    const response = await axios.delete(url, {
      headers: { "Content-Type": "application/json", ...headers },
      data,
      timeout: TIMEOUT,
    });
    return response?.data;
  } catch (error) {
    return handleApiError(error);
  }
};

/** Post FormData (multipart/form-data). Do not set Content-Type - axios sets it with boundary. */
export const apiPostForm = async (url, formData, headers = {}) => {
  try {
    const response = await axios.post(url, formData, {
      headers: { ...headers },
      timeout: 60000,
    });
    return response?.data;
  } catch (error) {
    return handleApiError(error);
  }
};

/** Put FormData (multipart/form-data). Do not set Content-Type - axios sets it with boundary. */
export const apiPutForm = async (url, formData, headers = {}) => {
  try {
    const response = await axios.put(url, formData, {
      headers: { ...headers },
      timeout: 60000,
    });
    return response?.data;
  } catch (error) {
    return handleApiError(error);
  }
};

const readBlobMessage = async (data, fallback) => {
  if (!(data instanceof Blob)) return data?.message || fallback;
  try {
    const text = await data.text();
    const json = JSON.parse(text);
    return json?.message || fallback;
  } catch {
    return fallback;
  }
};

/** GET a file. Auth header comes from the axios interceptor. Does not parse JSON. */
export const apiGetBlob = async (url, params = {}, timeout = 120000) => {
  try {
    const response = await axios.get(url, {
      params: Object.keys(params).length ? params : undefined,
      responseType: "blob",
      timeout,
    });
    const contentType = String(response.headers?.["content-type"] || "");
    if (contentType.includes("application/json")) {
      const message = await readBlobMessage(response.data, "Download failed");
      return { ok: false, httpStatus: response.status, message };
    }
    return {
      ok: true,
      httpStatus: response.status,
      blob: response.data,
      headers: response.headers,
    };
  } catch (error) {
    const message = await readBlobMessage(error?.response?.data, "Download failed");
    if (message === "Token is expired") {
      clearAuth();
      window.location.href = "/";
      return { ok: false, httpStatus: error?.response?.status, message };
    }
    if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
      return { ok: false, message: "Request timeout. Please try again." };
    }
    if (!error.response) {
      return { ok: false, message: "Network error. Please check your connection." };
    }
    return { ok: false, httpStatus: error?.response?.status, message };
  }
};

/** Returns headers with Authorization token for authenticated requests */
export const getAuthHeaders = () => {
  const token = getAuthItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
